/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Text,
    Button,
    Stack,
    Box,
    useStyleConfig,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useTagManager from '../../../hooks/use-tag-manager'
import {useCategory} from '@salesforce/commerce-sdk-react'
import LoadingSpinner from '../../loading-spinner'
import {FormattedMessage} from 'react-intl'
import {searchUrlBuilder} from '../../../utils/url'
import {useSiteContext} from '../../../contexts/site'

const Suggestions = ({
    suggestions,
    closeAndNavigate,
    isCategory,
    totalSuggestionsLength,
    enableBloomreachSuggestions,
    setRecommendedForText,
    phraseResponses,
    categoryResponses,
    setProductSuggestionsToRender,
    searchQuery
}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const suggestionsStyle = useStyleConfig('SuggestionsStyle')
    const {isYR} = useSiteContext()
    const [categoryId, setCategoryId] = useState('')
    const [fetchCategory, setFetchCategory] = useState(false)
    const {data: category, isLoading: isLoading} = useCategory(
        {
            parameters: {
                id: categoryId,
                levels: 1
            }
        },
        {
            keepPreviousData: true,
            enabled: !!fetchCategory && !!categoryId
        }
    )

    useEffect(() => {
        if (searchQuery && setRecommendedForText) {
            setRecommendedForText(searchQuery)
        }
    }, [searchQuery])

    useEffect(() => {
        if (fetchCategory && category) {
            const url = category.c_url
            const ksRedirection = url?.indexOf('/yellow-rose/') === -1
            const formattedUrl = ksRedirection ? url : url?.substring(url?.indexOf('/yellow-rose/'))
            setFetchCategory(false)
            closeAndNavigate(formattedUrl, false, !!category?.isExternalRedirection)
        }
    }, [fetchCategory, category])

    const handleAction = (displayText, query, isPhrase, formattedUrl, suggestion) => {
        if (isPhrase) {
            tagManager.siteSearch(
                searchQuery,
                totalSuggestionsLength?.toString() || '0',
                undefined,
                'selected-suggested-term',
                query
            )
            closeAndNavigate(searchUrlBuilder(isYR, query), true, false)
            return
        }
        if (isCategory) {
            tagManager.siteSearch(
                searchQuery,
                totalSuggestionsLength || 0,
                undefined,
                'categories',
                suggestion?.name?.replace(/<[^>]*>/g, '')
            )
        }
        if (suggestion?.isBloomreach) {
            setFetchCategory(true)
            setCategoryId(suggestion.id)
            return
        }
        closeAndNavigate(formattedUrl, false, !!suggestion?.isExternalRedirection)
    }

    const tagManager = useTagManager()
    if (!suggestions) {
        return null
    }
    return (
        <Stack spacing={0} data-testid="sf-suggestion" width="fit-content">
            {isLoading && fetchCategory && <LoadingSpinner />}
            <Box display="contents">
                {suggestions?.slice(0, 5)?.map((suggestion, idx) => {
                    const url = suggestion.link
                    const {displayText, query, isPhrase} = suggestion
                    const ksRedirection = url?.indexOf('/yellow-rose/') === -1
                    const formattedUrl = ksRedirection
                        ? url.split('.com')?.[1] || url
                        : url?.substring(url?.indexOf('/yellow-rose/'))

                    return (
                        <Button
                            aria-label="Suggestions"
                            onMouseEnter={() => {
                                if (
                                    phraseResponses?.[displayText]?.length &&
                                    enableBloomreachSuggestions &&
                                    isDesktop &&
                                    suggestion?.type != 'category'
                                ) {
                                    setProductSuggestionsToRender(phraseResponses[displayText])
                                    setRecommendedForText(displayText)
                                }
                                if (
                                    categoryResponses?.[suggestion.id]?.length &&
                                    enableBloomreachSuggestions &&
                                    isDesktop &&
                                    suggestion?.type === 'category'
                                ) {
                                    setProductSuggestionsToRender(categoryResponses[suggestion.id])
                                    setRecommendedForText(suggestion.name)
                                }
                            }}
                            onMouseDown={() =>
                                handleAction(displayText, query, isPhrase, formattedUrl, suggestion)
                            }
                            onKeyDown={(e) => {
                                const keys = new Set(['Enter', 'Space', 'NumpadEnter'])

                                if (e.code === 'Space') {
                                    e.preventDefault()
                                }

                                if (keys.has(e.code)) {
                                    handleAction(
                                        displayText,
                                        query,
                                        isPhrase,
                                        formattedUrl,
                                        suggestion
                                    )
                                }
                            }}
                            key={idx}
                            {...suggestionsStyle.term.button}
                            _hover={{
                                backgroundColor: isDesktop ? 'gray.100' : 'transparent',
                                borderRadius: '10px',
                                color: isDesktop ? 'teal.400' : 'black'
                            }}
                            transition="all, 0.3s ease"
                            padding="0px"
                            minWidth={{base: 'fit-content', lg: '380px'}}
                        >
                            <Text {...suggestionsStyle.term.text}>
                                {isPhrase ? (
                                    <FormattedMessage
                                        id="displayText"
                                        defaultMessage="Search for <b>{term}</b>"
                                        values={{
                                            b: (chunks) => (
                                                <Text
                                                    as="span"
                                                    fontWeight="600"
                                                    fontSize="14px"
                                                    textUnderlineOffset="4px"
                                                    letterSpacing="0.07px"
                                                    lineHeight="22px"
                                                    textDecoration="underline"
                                                >
                                                    {chunks}
                                                </Text>
                                            ),
                                            term: displayText
                                        }}
                                    />
                                ) : (
                                    suggestion?.name?.replace(/<[^>]*>/g, '')
                                )}
                            </Text>
                        </Button>
                    )
                })}
            </Box>
        </Stack>
    )
}

Suggestions.propTypes = {
    suggestions: PropTypes.array,
    closeAndNavigate: PropTypes.func,
    totalSuggestionsLength: PropTypes.any,
    searchQuery: PropTypes.string,
    setRecommendedForText: PropTypes.func,
    phraseResponses: PropTypes.any,
    categoryResponses: PropTypes.any,
    enableBloomreachSuggestions: PropTypes.bool,
    setProductSuggestions: PropTypes.func,
    setProductSuggestionsToRender: PropTypes.func,
    isCategory: PropTypes.bool
}

export default Suggestions
